import React, { FC, useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, Tooltip, XAxis, YAxis } from 'recharts';
import StableResponsiveContainer from '../general/stable-responsive-container/stable-responsive-container.component';
import { StatsResponse } from '../../models/stats.models';
import { getMeasurementUnit } from '../../helpers/general.helpers';
import { ZONE_CONFIGS } from '../../helpers/general.helpers';
import {
  buildPeriodZoneChartData,
  buildWeeklyZoneChartData,
  buildZoneDistributionData,
  formatDistributionPercent,
  formatDistributionRawValue,
  formatYAxisTick,
  formatZoneChartValue,
  getStackedBarYDomain,
  hasZoneData,
  MetricMode,
  StackedZoneChartRow,
  ZONE_DATA_KEYS,
  ZoneDistributionItem,
} from './training-zone-breakdown.helpers';
import './training-zone-breakdown.component.css';

interface TrainingZoneBreakdownProps {
  stats: StatsResponse | null;
  loading: boolean;
}

interface MetricToggleProps {
  value: MetricMode;
  onChange: (mode: MetricMode) => void;
}

const MetricToggle: FC<MetricToggleProps> = ({ value, onChange }) => (
  <div className="tzb-toggle">
    <button type="button" className={value === 'duration' ? 'active' : ''} onClick={() => onChange('duration')}>
      Duration
    </button>
    <button type="button" className={value === 'distance' ? 'active' : ''} onClick={() => onChange('distance')}>
      Distance
    </button>
  </div>
);

const ZoneLegend: FC = () => (
  <div className="tzb-zone-legend">
    {ZONE_CONFIGS.map((zone) => (
      <div className="tzb-zone-legend-item" key={zone.label}>
        <div className="tzb-zone-legend-dot" style={{ background: zone.barColor }} />
        {zone.label}
      </div>
    ))}
  </div>
);

interface ZoneStackedBarChartProps {
  data: StackedZoneChartRow[];
  mode: MetricMode;
  height: number;
  distanceUnit: string;
  xAxisInterval: number;
}

const ZoneStackedBarChart: FC<ZoneStackedBarChartProps> = ({ data, mode, height, distanceUnit, xAxisInterval }) => {
  const yDomain = useMemo(() => getStackedBarYDomain(data, mode), [data, mode]);

  if (data.length === 0) {
    return <div className="tzb-empty">No data</div>;
  }

  return (
    <div className="tzb-chart-container" style={{ height }}>
      <StableResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }} barCategoryGap="20%">
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 11, fill: '#6B7280' }}
            tickLine={false}
            axisLine={{ stroke: '#E5E7EB' }}
            interval={xAxisInterval}
          />
          <YAxis
            domain={yDomain}
            tick={{ fontSize: 10, fill: '#6B7280' }}
            tickLine={false}
            axisLine={false}
            width={48}
            tickFormatter={(value) => formatYAxisTick(value, mode, distanceUnit)}
          />
          <Tooltip
            content={({ active, payload, label }) => {
              if (!active || !payload?.length) {
                return null;
              }

              return (
                <div
                  style={{
                    background: '#fff',
                    border: '1px solid #E5E7EB',
                    borderRadius: 8,
                    padding: '8px 12px',
                    fontSize: 12,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: 6, color: '#111827' }}>{label}</div>
                  {payload
                    .filter((entry) => Number(entry.value) > 0)
                    .map((entry) => {
                      const zoneIndex = ZONE_DATA_KEYS.indexOf(entry.dataKey as (typeof ZONE_DATA_KEYS)[number]);
                      const zoneLabel = zoneIndex >= 0 ? ZONE_CONFIGS[zoneIndex].label : String(entry.name);
                      return (
                        <div
                          key={String(entry.dataKey)}
                          style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}
                        >
                          <span
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: 2,
                              background: entry.color,
                              flexShrink: 0,
                            }}
                          />
                          <span style={{ color: '#6B7280', minWidth: 24 }}>{zoneLabel}</span>
                          <span style={{ fontWeight: 600, color: '#111827' }}>
                            {formatZoneChartValue(Number(entry.value), mode, distanceUnit)}
                          </span>
                        </div>
                      );
                    })}
                </div>
              );
            }}
          />
          {ZONE_DATA_KEYS.map((key, index) => (
            <Bar
              key={key}
              dataKey={key}
              stackId="zones"
              fill={ZONE_CONFIGS[index].barColor}
              isAnimationActive={false}
              radius={index === ZONE_DATA_KEYS.length - 1 ? [2, 2, 0, 0] : undefined}
            />
          ))}
        </BarChart>
      </StableResponsiveContainer>
    </div>
  );
};

interface ZoneDistributionChartProps {
  data: ZoneDistributionItem[];
  mode: MetricMode;
  distanceUnit: string;
}

const ZoneDistributionChart: FC<ZoneDistributionChartProps> = ({ data, mode, distanceUnit }) => {
  const legendData = useMemo(() => [...data].reverse(), [data]);

  if (data.every((item) => item.rawValue === 0)) {
    return <div className="tzb-empty">No zone data</div>;
  }

  return (
    <div className="tzb-distribution">
      <div className="tzb-donut-container">
        <StableResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={48} outerRadius={72}>
              {data.map((entry) => (
                <Cell key={entry.zone} fill={entry.color} />
              ))}
            </Pie>
          </PieChart>
        </StableResponsiveContainer>
      </div>
      <div className="tzb-donut-legend">
        {legendData.map((item) => (
          <div className="tzb-donut-legend-row" key={item.zone}>
            <div className="tzb-donut-legend-dot" style={{ background: item.color }} />
            <div className="tzb-donut-legend-zone">{item.name}</div>
            <div className="tzb-donut-legend-pct">{formatDistributionPercent(item.percent)}</div>
            <div className="tzb-donut-legend-value">
              {formatDistributionRawValue(item.rawValue, mode, distanceUnit)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const TrainingZoneBreakdown: FC<TrainingZoneBreakdownProps> = ({ stats, loading }) => {
  const [weeklyMetric, setWeeklyMetric] = useState<MetricMode>('duration');
  const [periodMetric, setPeriodMetric] = useState<MetricMode>('duration');
  const [distributionMetric, setDistributionMetric] = useState<MetricMode>('duration');

  const distanceUnit = getMeasurementUnit();

  const weeklyData = useMemo(
    () => buildWeeklyZoneChartData(stats?.weekBreakdown ?? [], weeklyMetric),
    [stats?.weekBreakdown, weeklyMetric]
  );

  const periodData = useMemo(
    () => buildPeriodZoneChartData(stats?.periodBreakdown ?? [], periodMetric),
    [stats?.periodBreakdown, periodMetric]
  );

  const distributionData = useMemo(
    () => buildZoneDistributionData(stats?.totalDurationBreakdown, stats?.totalDistanceBreakdown, distributionMetric),
    [stats?.totalDurationBreakdown, stats?.totalDistanceBreakdown, distributionMetric]
  );

  const weeklySubtitle =
    weeklyMetric === 'duration' ? 'Hours in each zone per week' : `Distance in each zone per week (${distanceUnit})`;
  const periodSubtitle =
    periodMetric === 'duration'
      ? 'Hours in each zone per mesocycle'
      : `Distance in each zone per mesocycle (${distanceUnit})`;
  const distributionSubtitle =
    distributionMetric === 'duration'
      ? 'Percentage of total training time in each heart rate zone'
      : 'Percentage of total training distance in each heart rate zone';

  if (loading) {
    return (
      <div className="tzb-card">
        <div className="tzb-header">
          <div>
            <div className="tzb-title">Training Zone Breakdown</div>
            <div className="tzb-subtitle">Hours in each zone per week</div>
          </div>
        </div>
        <div className="tzb-loading">
          <div className="tzb-skeleton tzb-skeleton--chart" />
          <div className="tzb-bottom-grid">
            <div className="tzb-skeleton tzb-skeleton--chart-sm" />
            <div className="tzb-skeleton tzb-skeleton--chart-sm" />
          </div>
        </div>
      </div>
    );
  }

  if (!hasZoneData(stats)) {
    return (
      <div className="tzb-card">
        <div className="tzb-header">
          <div>
            <div className="tzb-title">Training Zone Breakdown</div>
            <div className="tzb-subtitle">Hours in each zone per week</div>
          </div>
        </div>
        <div className="tzb-empty">No zone data for this season</div>
      </div>
    );
  }

  return (
    <div className="tzb-card">
      <div className="tzb-header">
        <div>
          <div className="tzb-title">Training Zone Breakdown</div>
          <div className="tzb-subtitle">{weeklySubtitle}</div>
        </div>
        <MetricToggle value={weeklyMetric} onChange={setWeeklyMetric} />
      </div>

      <ZoneLegend />
      <ZoneStackedBarChart
        data={weeklyData}
        mode={weeklyMetric}
        height={260}
        distanceUnit={distanceUnit}
        xAxisInterval={1}
      />

      <div className="tzb-bottom-grid">
        <div>
          <div className="tzb-section-header">
            <div>
              <div className="tzb-section-title">Training Zones by Period</div>
              <div className="tzb-subtitle">{periodSubtitle}</div>
            </div>
            <MetricToggle value={periodMetric} onChange={setPeriodMetric} />
          </div>
          <ZoneLegend />
          <ZoneStackedBarChart
            data={periodData}
            mode={periodMetric}
            height={200}
            distanceUnit={distanceUnit}
            xAxisInterval={0}
          />
        </div>

        <div>
          <div className="tzb-section-header">
            <div>
              <div className="tzb-section-title">Zone Distribution — Yearlong</div>
              <div className="tzb-subtitle">{distributionSubtitle}</div>
            </div>
            <MetricToggle value={distributionMetric} onChange={setDistributionMetric} />
          </div>
          <ZoneDistributionChart data={distributionData} mode={distributionMetric} distanceUnit={distanceUnit} />
        </div>
      </div>
    </div>
  );
};

export default TrainingZoneBreakdown;
